import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import MuiCheckBoxGroup from "Components/MuiCheckboxGroup/MuiCheckboxGroup";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { convertToEnglishNumber } from "Helpers/convertToEnglishNumber";
import { useDispatch, useSelector } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import { getAccountingDefaultPanelAgain } from "Stores/actions";

export default function AddDefaultPanel({ disabled }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { activePeriods } = useSelector((state) => ({
    activePeriods: state.saAccounts.activePeriods,
  }));
  const [controls, setControls] = useSetState({
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    title: {
      type: "text",
      value: "",
      validation: {
        required: true,
      },
    },
    productCount: {
      type: "number",
      value: 0,
      validation: {
        required: true,
        isNumeric: true,
        notZero: true,
      },
    },
    options: {
      type: "checkbox",
      items: [
        { value: false, key: "isActive" },
        { value: false, key: "isDefault" },
      ],
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = () => {
    // reset variables
    setControls({ title: { value: "" } });
    setControls({ productCount: { value: 0 } });
    controls.options.items.map((items) => (items.value = false));
    setFormValid({ title: false, productCount: false });
    // fill selects
    if (activePeriods) {
      const _periods = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      setControls({
        periods: {
          selectItems: _periods,
          value: _periods[0],
        },
      });
    } else {
      showNotify({ text: t("getDataError"), customClass: "error" });
      onExit();
    }
  };

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
  };

  const checkboxHandler = (e, name, index) => {
    const items = controls[name].items;
    items[index] = { ...items[index], value: e.currentTarget.checked };
    setControls({ [name]: { items: items } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { periods, title, productCount, options } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const data = {
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          PeriodId: periods.value.value,
          Name: title.value,
          ProductCount: +convertToEnglishNumber(productCount.value),
          IsActive: options.items[0].value,
          IsDefault: options.items[1].value,
        };
        const response = await AccountService.postAccountingDefaultPanel(data);
        // console.log(response);
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            await dispatch(getAccountingDefaultPanelAgain());
            onExit();
          }
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } else {
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[AddDefaultPanel]", err);
      setLoading(false);
    }
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <BackDropLoading loading={loading} />
      <MuiButton
        color="primary"
        title={<Translator id="addDefaultPanel" />}
        innerIcon={<Icons.PlusLg />}
        onClick={onShowDialog}
        disabled={disabled || !read("FormDefaultPanel").accessRegister}
        hidden={!read("FormDefaultPanel").accessRegister}
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="xs"
      >
        <div className="dialogHeader">
          <p>{<Translator id={"addDefaultPanel"} />}</p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls)
              .filter((key) => controls[key].type !== "checkbox")
              .map((key) => {
                return (
                  <Grid item sm={controls[key].type === "select" ? 12 : 6} xs={12} key={key}>
                    {controls[key].type === "select" && (
                      <MuiAutoComplete
                        autoSelect={true}
                        options={controls[key].selectItems}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        onChange={autoCompleteHandler}
                        id={key}
                      />
                    )}
                    {controls[key].type === "text" && (
                      <Input
                        name={key}
                        setValue={setValue.bind(this)}
                        validation={controls[key].validation}
                        placeholder={t(key)}
                        label={<Translator id={key} />}
                        type="text"
                        checkValid={checkValid}
                        value={controls[key].value}
                      />
                    )}
                    {controls[key].type === "number" && (
                      <Input
                        name={key}
                        setValue={setValue.bind(this)}
                        validation={controls[key].validation}
                        placeholder={t(key)}
                        label={<Translator id={key} />}
                        type="text"
                        checkValid={checkValid}
                        value={controls[key].value}
                      />
                    )}
                  </Grid>
                );
              })}
          </Grid>
          <Grid container xs={12}>
            {Object.keys(controls)
              .filter((key) => controls[key].type === "checkbox")
              .map((key) => {
                return (
                  <Grid item xs={12} key={key}>
                    {controls[key].type === "checkbox" && (
                      <MuiCheckBoxGroup
                        translate={true}
                        name={key}
                        value={controls[key].value}
                        items={controls[key].items}
                        onChange={checkboxHandler}
                        className="horizontalCheckbox"
                      />
                    )}
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
